export const SUPPORTED_TAGS = [
  'main',
  'header',
  'section',
  'div',
  'span',
  'h1',
  'p',
  'button',
] as const

export type ElementTag = (typeof SUPPORTED_TAGS)[number]

export type NodeKind =
  | 'frame'
  | 'group'
  | 'text'
  | 'badge'
  | 'button'
  | 'metric'

export type StyleValue = string | number
export type StyleMap = Record<string, StyleValue>

export interface CanvasNode {
  id: string
  name: string
  tag: ElementTag
  kind: NodeKind
  text?: string
  styles: StyleMap
  children: string[]
}

export interface DesignTokens {
  background: string
  surface: string
  ink: string
  muted: string
  accent: string
  accentInk: string
  border: string
  radius: string
  font: string
}

export interface StudioDocument {
  id: string
  name: string
  rootId: string
  tokens: DesignTokens
  nodes: Record<string, CanvasNode>
  updatedAt: number
}

export interface DocumentPatch {
  nodes?: Record<
    string,
    Partial<Pick<CanvasNode, 'text' | 'styles' | 'children' | 'name'>>
  >
  tokens?: Partial<DesignTokens>
}

const baseTokens: DesignTokens = {
  background: '#f5f3ed',
  surface: '#ffffff',
  ink: '#151612',
  muted: '#66685f',
  accent: '#7069ff',
  accentInk: '#10110e',
  border: 'rgba(21, 22, 18, 0.14)',
  radius: '18px',
  font: "Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
}

function makeDocument(tokens: DesignTokens): StudioDocument {
  const nodes: Record<string, CanvasNode> = {
    page: {
      id: 'page',
      name: 'Launch page',
      tag: 'main',
      kind: 'frame',
      styles: {
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '28px 34px 26px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        fontFamily: tokens.font,
        background:
          `radial-gradient(circle at 82% 9%, ${tokens.accent}42 0, transparent 31%), ${tokens.background}`,
        color: tokens.ink,
      },
      children: ['navigation', 'hero', 'stats'],
    },
    navigation: {
      id: 'navigation',
      name: 'Navigation',
      tag: 'header',
      kind: 'group',
      styles: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '18px',
        marginBottom: '52px',
      },
      children: ['brand', 'navStatus'],
    },
    brand: {
      id: 'brand',
      name: 'Brand',
      tag: 'span',
      kind: 'text',
      text: 'NORTHSTAR / 01',
      styles: {
        fontSize: '13px',
        fontWeight: 760,
        letterSpacing: '0.09em',
      },
      children: [],
    },
    navStatus: {
      id: 'navStatus',
      name: 'Availability badge',
      tag: 'span',
      kind: 'badge',
      text: 'EARLY ACCESS · 24 SPOTS',
      styles: {
        padding: '8px 12px',
        border: `1px solid ${tokens.border}`,
        borderRadius: '999px',
        background: 'rgba(255, 255, 255, 0.54)',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.08em',
      },
      children: [],
    },
    hero: {
      id: 'hero',
      name: 'Hero',
      tag: 'section',
      kind: 'frame',
      styles: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        maxWidth: '760px',
      },
      children: [
        'eyebrow',
        'headline',
        'description',
        'actions',
        'signal',
      ],
    },
    eyebrow: {
      id: 'eyebrow',
      name: 'Eyebrow',
      tag: 'span',
      kind: 'badge',
      text: 'PRODUCT SYSTEMS FOR SMALL TEAMS',
      styles: {
        display: 'inline-flex',
        padding: '8px 11px',
        marginBottom: '18px',
        borderRadius: '6px',
        background: tokens.accent,
        color: tokens.accentInk,
        fontSize: '10px',
        fontWeight: 800,
        letterSpacing: '0.1em',
      },
      children: [],
    },
    headline: {
      id: 'headline',
      name: 'Headline',
      tag: 'h1',
      kind: 'text',
      text: 'Build the next thing.',
      styles: {
        maxWidth: '720px',
        margin: 0,
        fontSize: 'clamp(54px, 8.2vw, 94px)',
        fontWeight: 680,
        lineHeight: 0.91,
        letterSpacing: '-0.072em',
      },
      children: [],
    },
    description: {
      id: 'description',
      name: 'Description',
      tag: 'p',
      kind: 'text',
      text: 'A focused operating system for turning ambitious product bets into visible, repeatable momentum.',
      styles: {
        maxWidth: '520px',
        margin: '24px 0 0',
        color: tokens.muted,
        fontSize: '16px',
        lineHeight: 1.55,
        letterSpacing: '-0.015em',
      },
      children: [],
    },
    actions: {
      id: 'actions',
      name: 'Actions',
      tag: 'div',
      kind: 'group',
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginTop: '30px',
        flexWrap: 'wrap',
      },
      children: ['primaryAction', 'secondaryAction'],
    },
    primaryAction: {
      id: 'primaryAction',
      name: 'Primary action',
      tag: 'button',
      kind: 'button',
      text: 'Start a workspace  ↗',
      styles: {
        padding: '14px 18px',
        border: '0',
        borderRadius: '10px',
        background: tokens.ink,
        color: tokens.surface,
        fontFamily: 'inherit',
        fontSize: '13px',
        fontWeight: 720,
        cursor: 'pointer',
      },
      children: [],
    },
    secondaryAction: {
      id: 'secondaryAction',
      name: 'Secondary action',
      tag: 'button',
      kind: 'button',
      text: 'View the field notes',
      styles: {
        padding: '13px 17px',
        border: `1px solid ${tokens.border}`,
        borderRadius: '10px',
        background: 'rgba(255, 255, 255, 0.5)',
        color: tokens.ink,
        fontFamily: 'inherit',
        fontSize: '13px',
        fontWeight: 680,
        cursor: 'pointer',
      },
      children: [],
    },
    signal: {
      id: 'signal',
      name: 'Live signal',
      tag: 'div',
      kind: 'group',
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '22px',
        color: tokens.muted,
        fontSize: '11px',
        fontWeight: 650,
      },
      children: ['signalDot', 'signalText'],
    },
    signalDot: {
      id: 'signalDot',
      name: 'Signal dot',
      tag: 'span',
      kind: 'badge',
      text: '●',
      styles: {
        color: tokens.accent,
        fontSize: '14px',
      },
      children: [],
    },
    signalText: {
      id: 'signalText',
      name: 'Signal text',
      tag: 'span',
      kind: 'text',
      text: '14 launches shipped this week',
      styles: {},
      children: [],
    },
    stats: {
      id: 'stats',
      name: 'Impact metrics',
      tag: 'section',
      kind: 'group',
      styles: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '10px',
        marginTop: 'auto',
        paddingTop: '40px',
      },
      children: ['metricOne', 'metricTwo', 'metricThree'],
    },
    metricOne: {
      id: 'metricOne',
      name: 'Cycle time metric',
      tag: 'div',
      kind: 'metric',
      styles: {
        padding: '16px',
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        background: 'rgba(255, 255, 255, 0.52)',
      },
      children: ['metricOneValue', 'metricOneLabel'],
    },
    metricOneValue: {
      id: 'metricOneValue',
      name: 'Cycle time value',
      tag: 'span',
      kind: 'text',
      text: '−38%',
      styles: {
        display: 'block',
        fontSize: '25px',
        fontWeight: 720,
        letterSpacing: '-0.04em',
      },
      children: [],
    },
    metricOneLabel: {
      id: 'metricOneLabel',
      name: 'Cycle time label',
      tag: 'span',
      kind: 'text',
      text: 'cycle time',
      styles: {
        display: 'block',
        marginTop: '3px',
        color: tokens.muted,
        fontSize: '11px',
      },
      children: [],
    },
    metricTwo: {
      id: 'metricTwo',
      name: 'Signal metric',
      tag: 'div',
      kind: 'metric',
      styles: {
        padding: '16px',
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        background: 'rgba(255, 255, 255, 0.52)',
      },
      children: ['metricTwoValue', 'metricTwoLabel'],
    },
    metricTwoValue: {
      id: 'metricTwoValue',
      name: 'Signal value',
      tag: 'span',
      kind: 'text',
      text: '2.4×',
      styles: {
        display: 'block',
        fontSize: '25px',
        fontWeight: 720,
        letterSpacing: '-0.04em',
      },
      children: [],
    },
    metricTwoLabel: {
      id: 'metricTwoLabel',
      name: 'Signal label',
      tag: 'span',
      kind: 'text',
      text: 'faster signal',
      styles: {
        display: 'block',
        marginTop: '3px',
        color: tokens.muted,
        fontSize: '11px',
      },
      children: [],
    },
    metricThree: {
      id: 'metricThree',
      name: 'Focus metric',
      tag: 'div',
      kind: 'metric',
      styles: {
        padding: '16px',
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        background: 'rgba(255, 255, 255, 0.52)',
      },
      children: ['metricThreeValue', 'metricThreeLabel'],
    },
    metricThreeValue: {
      id: 'metricThreeValue',
      name: 'Focus value',
      tag: 'span',
      kind: 'text',
      text: '11h',
      styles: {
        display: 'block',
        fontSize: '25px',
        fontWeight: 720,
        letterSpacing: '-0.04em',
      },
      children: [],
    },
    metricThreeLabel: {
      id: 'metricThreeLabel',
      name: 'Focus label',
      tag: 'span',
      kind: 'text',
      text: 'focus reclaimed',
      styles: {
        display: 'block',
        marginTop: '3px',
        color: tokens.muted,
        fontSize: '11px',
      },
      children: [],
    },
  }

  return {
    id: 'northstar-launch',
    name: 'Northstar launch',
    rootId: 'page',
    tokens,
    nodes,
    updatedAt: Date.now(),
  }
}

export function createBaseDocument(): StudioDocument {
  return makeDocument({ ...baseTokens })
}

export function createInitialDocument(): StudioDocument {
  const base = createBaseDocument()
  const accent = '#c6ff5c'

  return applyDocumentPatch(base, {
    tokens: { accent, background: '#f3f1eb' },
    nodes: {
      page: {
        styles: {
          ...base.nodes.page.styles,
          background: `radial-gradient(circle at 82% 9%, ${accent}5c 0, transparent 31%), #f3f1eb`,
        },
      },
      eyebrow: {
        styles: { ...base.nodes.eyebrow.styles, background: accent },
      },
      headline: { text: 'Make momentum visible.' },
      signalDot: {
        styles: { ...base.nodes.signalDot.styles, color: accent },
      },
    },
  })
}

export function applyDocumentPatch(
  document: StudioDocument,
  patch: DocumentPatch,
): StudioDocument {
  const nextTokens = { ...document.tokens, ...patch.tokens }
  const nextNodes = { ...document.nodes }

  for (const [nodeId, nodePatch] of Object.entries(patch.nodes ?? {})) {
    const currentNode = nextNodes[nodeId]
    if (!currentNode) continue

    nextNodes[nodeId] = {
      ...currentNode,
      ...nodePatch,
      styles: nodePatch.styles
        ? { ...currentNode.styles, ...nodePatch.styles }
        : currentNode.styles,
      children: nodePatch.children
        ? [...nodePatch.children]
        : currentNode.children,
    }
  }

  return {
    ...document,
    tokens: nextTokens,
    nodes: nextNodes,
    updatedAt: Date.now(),
  }
}

export function isSupportedTag(value: string): value is ElementTag {
  return (SUPPORTED_TAGS as readonly string[]).includes(value)
}
